// ⑤ 承認状況ダッシュボード（デザイン正本 approval.jsx 準拠）
// 事務所視点：誰が承認済／閲覧済・未承認／未読／質問あり かを把握。承認APIから取得。

import React, { useEffect, useMemo, useState } from "react";
import { Ic } from "../components/Icons";
import { useStore } from "../store";
import { yen } from "../lib/normalize";
import { fetchApprovalStatus, type ApprovalSummary } from "../lib/approvalApi";
import { LoadingLabel, SkeletonBar, SkeletonTableRows } from "../components/Loading";
import type { Carrier } from "../types";

const CARRIER_LABEL: Record<Carrier, string> = { yamato: "ヤマト", sagawa: "佐川" };

// 表示用の4状態（API status → デザイン状態）
type ApState = "approved" | "viewed" | "unread" | "question";
const AP_DEF: Record<ApState, { label: string; cls: string; dot: string }> = {
  approved: { label: "承認済", cls: "ap-approved", dot: "var(--green)" },
  viewed: { label: "閲覧済・未承認", cls: "ap-viewed", dot: "var(--blue)" },
  unread: { label: "未読", cls: "ap-unread", dot: "#8089a0" },
  question: { label: "質問あり", cls: "ap-question", dot: "var(--amber)" },
};
function apStateOf(s: ApprovalSummary["status"]): ApState {
  if (s === "approved") return "approved";
  if (s === "viewed") return "viewed";
  if (s === "question") return "question";
  return "unread"; // published
}

const AVATAR_PALETTE: [string, string][] = [
  ["#e2ecfb", "#1d4ed8"], ["#fbf2cf", "#8a6d12"], ["#e7f4ec", "#1f8a4c"], ["#fdecea", "#cc2f2a"], ["#ede8fb", "#6d3fd1"],
];
function avatarColor(name: string): [string, string] {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[h];
}

function fmtStamp(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function ApChip({ state }: { state: ApState }) {
  const d = AP_DEF[state];
  return <span className={"apchip " + d.cls}><span className="dot" style={{ background: d.dot }} />{d.label}</span>;
}

function StatCard({ state, count, total, active, onClick }: { state: ApState; count: number; total: number; active: boolean; onClick: () => void }) {
  const d = AP_DEF[state];
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <button onClick={onClick} className={"card ap-statcard" + (active ? " active" : "")}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 9, height: 9, borderRadius: "50%", background: d.dot }} />
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink-2)" }}>{d.label}</span>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 5, marginTop: 8 }}>
        <span className="num" style={{ fontSize: 27, fontWeight: 800 }}>{count}</span>
        <span style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 700 }}>名 ・ {pct}%</span>
      </div>
    </button>
  );
}

export function ApprovalStatusScreen({ onNav, onSelect }: { onNav: (s: string) => void; onSelect: (driverId: string) => void }) {
  const { state } = useStore();
  const [items, setItems] = useState<ApprovalSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ApState | "all">("all");
  const [q, setQ] = useState("");
  const [reminded, setReminded] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  // ドライバー専用URL（token から組み立て）。発行後はいつでもここからコピーできる。
  const linkOf = (token: string) => `${window.location.origin}/?token=${token}`;
  const copyLink = async (token: string, key: string) => {
    try {
      await navigator.clipboard.writeText(linkOf(token));
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
    } catch {
      alert("コピーに失敗しました。URLを手動で選択してください。");
    }
  };

  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const run = async () => {
      const r = await fetchApprovalStatus(state.targetMonth);
      if (!alive) return;
      setItems(r.ok ? r.items : []);
      setLoading(false);
    };
    void run();
    // KV は発行直後の一覧反映に数秒の遅延があるため、少し後にもう一度取得して取りこぼしを防ぐ
    const t = setTimeout(() => { if (alive) void run(); }, 2500);
    return () => { alive = false; clearTimeout(t); };
  }, [state.targetMonth]);

  const manualRefresh = async () => {
    setRefreshing(true);
    const r = await fetchApprovalStatus(state.targetMonth);
    setItems(r.ok ? r.items : []);
    setRefreshing(false);
  };

  // driverId → statement（差引支給額・キャリアの参照用）
  const stmtById = useMemo(() => {
    const m = new Map<string, (typeof state.statements)[number]>();
    for (const s of state.statements) m.set(s.driverId, s);
    return m;
  }, [state.statements]);

  const rows = useMemo(() => {
    const list = (items || []).map((it) => {
      const st = stmtById.get(it.driverId);
      return {
        ...it,
        ap: apStateOf(it.status),
        net: st?.netTotal ?? null,
        carriers: st ? (st.carriers ?? [st.carrier]) : [],
        updatedAt: it.approvedAt || it.viewedAt || null,
      };
    });
    return list
      .filter((d) => filter === "all" || d.ap === filter)
      .filter((d) => !q.trim() || d.driverName.includes(q.trim()) || d.driverId.toLowerCase().includes(q.trim().toLowerCase()));
  }, [items, stmtById, filter, q]);

  const total = items?.length ?? 0;
  const counts = useMemo(() => {
    const c: Record<ApState, number> = { approved: 0, viewed: 0, unread: 0, question: 0 };
    for (const it of items || []) c[apStateOf(it.status)]++;
    return c;
  }, [items]);
  const approvedPct = total > 0 ? Math.round((counts.approved / total) * 100) : 0;
  const pending = counts.viewed + counts.unread + counts.question;
  const pendingNames = (items || []).filter((it) => apStateOf(it.status) !== "approved").map((it) => it.driverName);

  const issuedAt = useMemo(() => {
    const ts = (items || []).map((it) => it.publishedAt).filter(Boolean).sort();
    return ts.length > 0 ? fmtStamp(ts[0]) : "—";
  }, [items]);

  const seg: { k: ApState; w: number }[] = [
    { k: "approved", w: counts.approved },
    { k: "viewed", w: counts.viewed },
    { k: "question", w: counts.question },
    { k: "unread", w: counts.unread },
  ];

  const empty = !loading && total === 0;
  // 読み込み中は数字（0 / 0 名）を出さず、枠だけ見せる
  const showStats = !loading && !empty;

  return (
    <div className="content" style={{ maxWidth: 1380 }}>
      <div className="page-title-row">
        <div className="col gap-4">
          <div className="page-title">承認状況</div>
          <div className="page-sub muted row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
            <span>{formatMonth(state.targetMonth)}</span>
            {/* 取得が終わるまでは件数を出さない。読み込み中に「発行されていません」と出ると、
                実際は発行済みでも「無い」と誤解させてしまう（三浦さん指摘 2026-08-29） */}
            {loading ? (
              <LoadingLabel text="承認状況を読み込み中…" />
            ) : total > 0 ? (
              <span>
                ／ 承認依頼 発行 {issuedAt} ／ 承認済 <b style={{ color: "var(--ink)" }}>{counts.approved} / {total} 名</b>
              </span>
            ) : (
              <span>／ 承認依頼はまだ発行されていません</span>
            )}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn" onClick={() => onNav("home")}><Ic.chevronL size={13} /> ホーム</button>
        <button className="btn" onClick={manualRefresh} disabled={refreshing} title="最新の承認状況を取得（発行直後はKV反映に数秒かかることがあります）">
          <Ic.history size={14} /> {refreshing ? "更新中..." : "更新"}
        </button>
        <button className="btn primary" onClick={() => setReminded(true)} disabled={reminded || pending === 0}>
          <Ic.send size={14} /> {reminded ? "催促リストを表示しました" : `未承認者へ催促 (${pending})`}
        </button>
      </div>

      {empty && (
        <div className="toast info">
          <Ic.info size={14} />
          <div className="col gap-4" style={{ flex: 1 }}>
            <b>この月の承認依頼はまだ発行されていません。</b>
            <div className="tiny">「支払明細 一覧」で明細を確定し、「承認用に発行」すると、ここに承認状況が表示されます。</div>
          </div>
          <button className="btn sm" onClick={() => onNav("list")}>一覧へ</button>
        </div>
      )}

      {reminded && pending > 0 && (
        <div className="toast warn">
          <Ic.alert size={14} />
          <div className="col gap-4" style={{ flex: 1 }}>
            <b>未承認 {pending} 名へ催促してください（配布済みURLから個別にご連絡）</b>
            <div className="tiny">{pendingNames.join("、")}</div>
          </div>
        </div>
      )}

      {loading && (
        <div className="card" style={{ padding: "22px 26px", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <SkeletonBar w={64} h={34} />
            <SkeletonBar w={150} h={16} />
          </div>
          <SkeletonBar w="100%" h={10} />
          <div style={{ display: "flex", gap: 18, marginTop: 14 }}>
            <SkeletonBar w={90} h={11} />
            <SkeletonBar w={110} h={11} />
            <SkeletonBar w={90} h={11} />
            <SkeletonBar w={80} h={11} />
          </div>
        </div>
      )}

      {showStats && (
        <>
          {/* 進捗ヒーロー */}
          <div className="card" style={{ padding: "22px 26px", marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                <span className="num" style={{ fontSize: 40, fontWeight: 800, lineHeight: 1 }}>{counts.approved}</span>
                <span style={{ fontSize: 16, color: "var(--ink-3)", fontWeight: 700 }}>/ {total} 名 承認済</span>
                <span className="chip green" style={{ marginLeft: 6 }}>{approvedPct}%</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--ink-3)", fontWeight: 700 }}>
                <Ic.clock size={16} /> 承認はドライバーが本人専用URLから行います
              </div>
            </div>
            <div className="pbar">
              {seg.map((g) => g.w > 0 && <span key={g.k} style={{ width: (g.w / total * 100) + "%", background: AP_DEF[g.k].dot }} />)}
            </div>
            <div style={{ display: "flex", gap: 18, marginTop: 12, flexWrap: "wrap" }}>
              {seg.map((g) => (
                <div key={g.k} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--ink-2)", fontWeight: 600 }}>
                  <span style={{ width: 9, height: 9, borderRadius: "50%", background: AP_DEF[g.k].dot }} />
                  {AP_DEF[g.k].label} <b className="num">{g.w}</b>
                </div>
              ))}
            </div>
          </div>

          {/* ステータスカード（フィルタ） */}
          <div className="ap-statgrid">
            {(["approved", "viewed", "question", "unread"] as ApState[]).map((k) => (
              <StatCard key={k} state={k} count={counts[k]} total={total} active={filter === k} onClick={() => setFilter(filter === k ? "all" : k)} />
            ))}
          </div>

          {/* 検索 */}
          <div className="filter-bar">
            <div className="search-wrap" style={{ flex: "0 0 320px" }}>
              <Ic.search size={14} />
              <input className="input search" placeholder="氏名 / IDで検索" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            {filter !== "all" && (
              <button className="btn sm" onClick={() => setFilter("all")}>絞り込み解除（{AP_DEF[filter].label}）✕</button>
            )}
            <div style={{ flex: 1 }} />
            <span className="tiny muted num">{rows.length} 名</span>
          </div>

          {/* テーブル */}
          <div className="card list-card tbl-cards-wrap">
            <div className="tbl-scroll">
              <table className="tbl list-tbl tbl-cards">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>氏名</th>
                    <th>キャリア</th>
                    <th className="num">差引支給額</th>
                    <th>承認状況</th>
                    <th>更新</th>
                    <th>リンク</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((d) => {
                    const [bg, fg] = avatarColor(d.driverName);
                    return (
                      <tr key={d.driverId} style={{ cursor: "pointer" }} onClick={() => onSelect(d.driverId)}>
                        <td className="mono muted" data-label="ID">{d.driverId}</td>
                        <td className="card-title-cell">
                          <div className="row gap-8" style={{ alignItems: "center" }}>
                            <span className="initial-bubble" style={{ background: bg, color: fg }}>{d.driverName[0]}</span>
                            <div className="col">
                              <span style={{ fontWeight: 700 }}>{d.driverName}</span>
                              {d.ap === "question" && d.question && (
                                <span className="tiny" style={{ color: "var(--amber)", fontWeight: 600 }}>「{d.question}」</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td data-label="キャリア">
                          <div className="row gap-4" style={{ flexWrap: "wrap" }}>
                            {d.carriers.map((c) => <span key={c} className={`chip carrier-${c}`}>{CARRIER_LABEL[c]}</span>)}
                          </div>
                        </td>
                        <td className="num net-cell" data-label="差引支給額" style={{ fontWeight: 700 }}>{d.net != null ? yen(d.net) : "—"}</td>
                        <td data-label="承認状況"><ApChip state={d.ap} /></td>
                        <td className="num tiny muted" data-label="更新">{fmtStamp(d.updatedAt)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <button
                            className="btn sm ghost"
                            onClick={() => copyLink(d.token, d.driverId)}
                            title="このドライバー専用の承認URLをコピー（LINE等で本人へ送る）"
                          >
                            {copied === d.driverId ? "✓ コピー" : <><Ic.doc size={12} /> リンク</>}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: 14, fontSize: 12, color: "var(--ink-3)" }}>
            ドライバーが見るスマホ画面は <a onClick={() => onNav("approvalPreview")} style={{ color: "var(--blue)", fontWeight: 700, cursor: "pointer" }}>ドライバー承認（プレビュー）</a> で確認できます
          </div>
        </>
      )}
    </div>
  );
}

function formatMonth(ym: string): string {
  if (!/^\d{4}-\d{2}$/.test(ym)) return ym;
  const [y, m] = ym.split("-").map(Number);
  return `${y}年${m}月`;
}
